import { START_DATE_ALIAS_FIELD_ID } from '@atlassian/jira-business-constants';
import {
	ASSIGNEE_TYPE,
	DUE_DATE_TYPE,
	ISSUE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
	SUMMARY_TYPE,
} from '@atlassian/jira-platform-field-config';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import type { CalendarIssue } from '../../../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getField = (field?: any | null) => {
	const fieldId = field?.aliasFieldId || field?.fieldId;
	switch (fieldId) {
		case 'issuekey': // not available to be imported from jira-platform-field-config
			return {
				key: field.text,
			};

		case ISSUE_TYPE:
			return {
				iconUrl: field.issueType?.avatar.medium,
				issueTypeId: field.issueType.issueTypeId,
				issueTypeName: field.issueType.name,
			};

		case SUMMARY_TYPE:
			return {
				summary: field.text,
			};

		case STATUS_TYPE:
			return {
				statusName: field.status.name,
				statusCategoryId: Number(field.status.statusCategory.statusCategoryId),
			};

		case START_DATE_ALIAS_FIELD_ID:
		case DUE_DATE_TYPE:
			return {
				[fieldId]: (field.date && DateOnly.fromISODateString(field.date)) ?? null,
			};

		case ASSIGNEE_TYPE:
			return {
				assignee:
					(field.user && {
						avatarUrl: field.user.picture,
						displayName: field.user.name,
					}) ??
					null,
			};

		case PRIORITY_TYPE:
			return {
				priority:
					(field.priority && {
						name: field.priority.name,
						iconUrl: field.priority.iconUrl,
						priorityId: field.fieldId,
					}) ??
					null,
			};

		default:
			return {};
	}
};

export function transformData<Edges, TransformedItem>({
	edges,
	areUnscheduledItems,
}: {
	edges: Edges;
	areUnscheduledItems: boolean;
}): TransformedItem[] | null {
	if (edges == null || !Array.isArray(edges)) return null;

	return edges.reduce<TransformedItem[]>((transformedData, edge) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const populateItem = (acc: CalendarIssue, field?: any | null) => ({
			...acc,
			...getField(field),
		});

		if (Array.isArray(edge.node?.fields)) {
			const item = edge.node.fields.reduce(populateItem, {
				id: edge.node.issueId,
				duedate: null,
				priority: null,
				startdate: null,
			});

			if (!item.startdate && item.duedate) {
				item.startdate = item.duedate;
			}

			// TODO: We might be able to get rid of this as filtering is happening through JQL. (https://jbusiness.atlassian.net/browse/JER-158)
			if (
				(areUnscheduledItems === true && item.duedate === null) ||
				(!areUnscheduledItems && item.duedate != null)
			) {
				transformedData.push(item);
			}
		}

		return transformedData;
	}, []);
}
